import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, CircleCheck } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { MiniStat, ScoreRing, scoreTone } from '../../../components/design-system/analytics';
import { SectionCard, StatusBadge, StatusTone, formatNumber } from '../../../components/design-system/admin';
import { MarketData } from '../../../common/hooks/use-market-api.hook';
import { AuditIssue, SEVERITIES, Severity, SnippetLevel, VitalKey, VitalLevel, classifyAudit, humaniseCheck, snippetFit, vitalLevel } from '../../../modules/vendor/market/health-checks';
import { healthInsights } from '../../../modules/vendor/market/market-insights';
import { Nothing, OutLink, Reading, Term, TermKey } from './market-shared';

/** The same bands the written reading uses (healthInsights): 80 and up is good, under 60 needs work. */
export const HEALTH_THRESHOLDS = { good: 80, bad: 60 };

const SEVERITY_TONE: Record<Severity, StatusTone> = { error: 'danger', warning: 'warning', notice: 'neutral' };
const VITAL_TONE: Record<VitalLevel, StatusTone> = { good: 'success', watch: 'warning', bad: 'danger' };
const SNIPPET_TONE: Record<SnippetLevel, StatusTone> = { good: 'success', short: 'warning', long: 'warning', missing: 'danger' };

/**
 * The home page, audited — read like a site audit.
 *
 * The score and its reading, then the counts by severity and the page's
 * speed against Google's own thresholds, then how the page would look in a
 * results list, then every issue with why it matters and how to fix it. The
 * checks the API reports backwards (HTTPS, readable address) are shown as
 * what they are: passes.
 */
export const HealthView: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t } = useTranslation();
    const audit = data.pageAudit;
    const specs = useMemo(() => healthInsights(audit), [audit]);
    const { issues, passed, counts } = useMemo(() => classifyAudit(audit), [audit]);

    if (!audit) return <Nothing title={t('MARKET.HEALTH.NONE')} description={t('BUSINESS.HEALTH.NONE_HINT')} />;

    const timing = audit.timing ?? {};
    const seconds = (ms: number) => t('BUSINESS.HEALTH.SECONDS', { value: formatNumber(ms / 1000, 1) });
    const vitals: { key: VitalKey; term: TermKey; value: number | null | undefined; format: (v: number) => string }[] = [
        { key: 'largestPaint', term: 'LCP', value: timing.largestPaint, format: seconds },
        { key: 'interactive', term: 'TTI', value: timing.interactive, format: seconds },
        { key: 'domComplete', term: 'DOM', value: timing.domComplete, format: seconds },
        { key: 'layoutShift', term: 'CLS', value: timing.layoutShift, format: (v) => formatNumber(v, 2) },
    ];
    const measured = vitals.filter((v) => vitalLevel(v.key, v.value) != null);

    return (
        <div className="flex flex-col gap-6">
            <Reading specs={specs} />

            <div className="grid gap-4 lg:grid-cols-3">
                <SectionCard title={<Term k="HEALTH" />} testId="business-health-score">
                    <ScoreRing
                        value={audit.score ?? null}
                        size={112}
                        tone={scoreTone(audit.score, HEALTH_THRESHOLDS)}
                        label={t('BUSINESS.HEALTH.SCORE_LABEL')}
                        sublabel={
                            audit.url ? (
                                <OutLink href={audit.url} className="max-w-full">
                                    {audit.url.replace(/^https?:\/\//, '')}
                                </OutLink>
                            ) : undefined
                        }
                    />
                </SectionCard>

                <SectionCard title={t('BUSINESS.HEALTH.ISSUES')} description={t('BUSINESS.HEALTH.ISSUES_HINT')}>
                    <div className="grid grid-cols-3 gap-4">
                        {SEVERITIES.map((s) => (
                            <MiniStat
                                key={s}
                                label={
                                    <span className="inline-flex items-center gap-1.5">
                                        <span
                                            className={cn('size-1.5 rounded-full', s === 'error' ? 'bg-destructive' : s === 'warning' ? 'bg-warning' : 'bg-muted-foreground/50')}
                                            aria-hidden
                                        />
                                        {t(`BUSINESS.HEALTH.SEVERITY.${s.toUpperCase()}`)}
                                    </span>
                                }
                                value={formatNumber(counts[s])}
                            />
                        ))}
                    </div>
                    <MiniStat className="mt-5 border-t pt-4" label={t('MARKET.HEALTH.ERRORS')} value={formatNumber(audit.errors ?? 0)} sub={t('MARKET.HEALTH.ERRORS_HINT')} />
                </SectionCard>

                <SectionCard title={t('BUSINESS.HEALTH.SPEED')} description={t('BUSINESS.HEALTH.SPEED_HINT')}>
                    {measured.length ? (
                        <ul className="flex flex-col divide-y">
                            {measured.map((v) => {
                                const level = vitalLevel(v.key, v.value) as VitalLevel;
                                return (
                                    <li key={v.key} className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0">
                                        <Term k={v.term} className="text-sm" />
                                        <span className="flex items-center gap-3">
                                            <span className="text-sm font-medium tabular-nums">{v.format(v.value as number)}</span>
                                            <StatusBadge tone={VITAL_TONE[level]} className="w-20">
                                                {t(`BUSINESS.HEALTH.VITAL.${level.toUpperCase()}`)}
                                            </StatusBadge>
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className="text-sm text-muted-foreground">{t('BUSINESS.COMMON.NOT_IN_SNAPSHOT')}</p>
                    )}
                </SectionCard>
            </div>

            <SnippetPreview url={audit.url} title={audit.title} description={audit.description} />

            <SectionCard flush title={t('BUSINESS.HEALTH.ALL_ISSUES')} description={t('BUSINESS.HEALTH.ALL_ISSUES_HINT')} testId="business-issues">
                {issues.length === 0 ? (
                    <p className="flex items-center gap-2 border-t px-4 py-6 text-sm text-muted-foreground">
                        <CircleCheck className="size-4 text-success" />
                        {t('MARKET.HEALTH.ALL_PASSED')}
                    </p>
                ) : (
                    SEVERITIES.filter((s) => counts[s] > 0).map((s) => (
                        <div key={s} className="border-t">
                            <p className="flex items-center gap-2 bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground">
                                <StatusBadge tone={SEVERITY_TONE[s]}>{t(`BUSINESS.HEALTH.SEVERITY.${s.toUpperCase()}`)}</StatusBadge>
                                <span className="tabular-nums">{formatNumber(counts[s])}</span>
                                <span className="font-normal">· {t(`BUSINESS.HEALTH.SEVERITY.${s.toUpperCase()}_HINT`)}</span>
                            </p>
                            <ul className="divide-y">
                                {issues
                                    .filter((i) => i.severity === s)
                                    .map((i) => (
                                        <IssueRow key={i.check} issue={i} />
                                    ))}
                            </ul>
                        </div>
                    ))
                )}
            </SectionCard>

            {passed.length > 0 && (
                <SectionCard title={t('BUSINESS.HEALTH.PASSED')} description={t('BUSINESS.HEALTH.PASSED_HINT')}>
                    <ul className="grid gap-2 sm:grid-cols-2">
                        {passed.map((p) => (
                            <li key={p} className="flex items-start gap-2 text-sm">
                                <CircleCheck className="mt-0.5 size-4 shrink-0 text-success" />
                                {t(`BUSINESS.HEALTH.PASS.${p.toUpperCase()}`, { defaultValue: humaniseCheck(p) })}
                            </li>
                        ))}
                    </ul>
                </SectionCard>
            )}
        </div>
    );
};

/** One failed check: its name, its area, and — opened — why it matters and how to fix it. */
const IssueRow: React.FC<{ issue: AuditIssue }> = ({ issue }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const key = `BUSINESS.HEALTH.CHECK.${issue.check.toUpperCase()}`;
    const title = t(`${key}.TITLE`, { defaultValue: humaniseCheck(issue.check) });
    const generic = `BUSINESS.HEALTH.GENERIC.${issue.severity.toUpperCase()}`;
    const why = issue.detailed ? t(`${key}.WHY`) : t(`${generic}.WHY`);
    const fix = issue.detailed ? t(`${key}.FIX`) : t(`${generic}.FIX`);

    return (
        <li>
            <Collapsible open={open} onOpenChange={setOpen}>
                <div className="flex items-center gap-3 px-4 py-3">
                    <span
                        className={cn('size-2 shrink-0 rounded-full', issue.severity === 'error' ? 'bg-destructive' : issue.severity === 'warning' ? 'bg-warning' : 'bg-muted-foreground/50')}
                        aria-hidden
                    />
                    <span className="min-w-0 flex-1 text-sm">{title}</span>
                    <span className="hidden shrink-0 rounded-full border px-2 text-[11px] leading-5 text-muted-foreground sm:inline">
                        {t(`BUSINESS.HEALTH.THEME.${issue.theme.toUpperCase()}`)}
                    </span>
                    <CollapsibleTrigger asChild>
                        <button
                            type="button"
                            className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                            aria-expanded={open}
                        >
                            {t('BUSINESS.HEALTH.HOW_TO_FIX')}
                            <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
                        </button>
                    </CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                    <div className="grid gap-3 px-4 pb-4 ps-9 sm:grid-cols-2">
                        <div>
                            <p className="text-xs font-medium text-muted-foreground">{t('BUSINESS.HEALTH.WHY')}</p>
                            <p className="mt-1 text-sm leading-relaxed">{why}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-muted-foreground">{t('BUSINESS.HEALTH.FIX')}</p>
                            <p className="mt-1 text-sm leading-relaxed">{fix}</p>
                        </div>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </li>
    );
};

/** The page as a results list might show it, with whether each part fits. */
const SnippetPreview: React.FC<{ url?: string | null; title?: string | null; description?: string | null }> = ({ url, title, description }) => {
    const { t } = useTranslation();
    const titleFit = snippetFit(title, 'title');
    const descFit = snippetFit(description, 'description');
    const clip = (s: string, max: number) => (s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s);
    return (
        <SectionCard title={t('BUSINESS.HEALTH.SNIPPET')} description={t('BUSINESS.HEALTH.SNIPPET_HINT')} testId="business-snippet">
            <div className="grid gap-5 lg:grid-cols-5">
                <div className="min-w-0 rounded-lg border p-4 lg:col-span-3">
                    <p className="truncate text-xs text-muted-foreground" dir="ltr">
                        {(url || '').replace(/^https?:\/\//, '').replace(/\/$/, '') || '—'}
                    </p>
                    <p className="mt-1 line-clamp-2 text-[17px] leading-snug">{title ? clip(title, 62) : <span className="text-muted-foreground">{t('BUSINESS.HEALTH.NO_TITLE')}</span>}</p>
                    <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                        {description ? clip(description, 162) : t('BUSINESS.HEALTH.NO_DESCRIPTION')}
                    </p>
                </div>
                <dl className="flex flex-col gap-3 lg:col-span-2">
                    {[
                        { key: 'TITLE', fit: titleFit, range: '30–60' },
                        { key: 'DESCRIPTION', fit: descFit, range: '70–160' },
                    ].map((row) => (
                        <div key={row.key} className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <dt className="text-sm">{t(`BUSINESS.HEALTH.SNIPPET_${row.key}`)}</dt>
                                <dd className="mt-0.5 text-xs text-muted-foreground">
                                    {t('BUSINESS.HEALTH.SNIPPET_LENGTH', { length: formatNumber(row.fit.length), range: row.range })}
                                </dd>
                            </div>
                            <StatusBadge tone={SNIPPET_TONE[row.fit.level]}>{t(`BUSINESS.HEALTH.FIT.${row.fit.level.toUpperCase()}`)}</StatusBadge>
                        </div>
                    ))}
                </dl>
            </div>
        </SectionCard>
    );
};
